/**
 * Route / telematics helpers for the Profit vehicle-day map.
 * Extracted from ProfitabilityReport to avoid circular imports with ProfitVehicleDayMapModal.
 */

import { standardiseVehicleId } from './vehicleIdStandardise';
import { haversineMetres, normalizeUtcTimestamp } from './vehicleDayPlaybackTrack';
import type { OrderHandoffMapPoint } from './mapOrderVehicleRole';
import {
  getDateOnly,
  normalizeTelematicsDate,
  type ProfitMapOrder,
  type TelematicsDeliveryStop,
  type RouteSegment,
} from './profitVehicleDayMap';

export function buildRouteSegmentSelectionId(
  vehicleId: string | null | undefined,
  date: string | null | undefined,
  fromStopNumber: number | null | undefined,
  toStopNumber: number | null | undefined,
  ordinal?: number | string,
): string {
  const safeVehicleId = standardiseVehicleId(vehicleId ?? '') || String(vehicleId ?? '').trim() || 'unknown-vehicle';
  const safeDate = String(date ?? '').trim() || 'unknown-date';
  const safeFrom = fromStopNumber ?? 'unknown-from';
  const safeTo = toStopNumber ?? 'unknown-to';
  const suffix = ordinal == null ? '' : `-${String(ordinal)}`;
  return `seg-${safeVehicleId}-${safeDate}-${safeFrom}-${safeTo}${suffix}`;
}

/** Stable MapRoute / sidebar id for order-map telematics segments (must match). */
export function orderMapSegmentSelectionId(
  seg: {
    id?: string | null;
    vehicle?: string | null;
    date?: string | null;
    from_stop_number?: number | null;
    to_stop_number?: number | null;
    label?: string | null;
  },
  idx: number,
): string {
  const rawId = seg.id != null ? String(seg.id).trim() : '';
  if (rawId && rawId !== 'undefined') {
    // MapLibre layer/source ids are safer without whitespace.
    return rawId.replace(/\s+/g, '-');
  }
  const labelSlug = String(seg.label ?? 'leg')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9_-]/g, '') || 'leg';
  return buildRouteSegmentSelectionId(
    seg.vehicle,
    seg.date,
    seg.from_stop_number,
    seg.to_stop_number,
    `${labelSlug}-${idx}`,
  );
}

/**
 * Map polyline ids that sit on the path between two visible vehicle-map stops.
 * Sidebar lists logical spans (Start→Delivery→…); the map draws every GPS edge —
 * selection must highlight all GPS edges in that span, not invent a non-existent id.
 */
export function mapSegmentIdsBetweenVisibleStops(
  segments: Array<{
    vehicle?: string | null;
    date?: string | null;
    from_stop_number?: number | null;
    to_stop_number?: number | null;
  }>,
  fromStop: Pick<TelematicsDeliveryStop, 'AssetName' | 'day_date' | 'stop_number'>,
  toStop: Pick<TelematicsDeliveryStop, 'AssetName' | 'day_date' | 'stop_number'>,
): string[] {
  const vehicleId = standardiseVehicleId(fromStop.AssetName);
  if (!vehicleId) return [];
  const fromDate = normalizeTelematicsDate(String(fromStop.day_date ?? ''));
  const toDate = normalizeTelematicsDate(String(toStop.day_date ?? ''));
  const ids: string[] = [];
  segments.forEach((seg, idx) => {
    if (standardiseVehicleId(seg.vehicle ?? '') !== vehicleId) return;
    const segDate = normalizeTelematicsDate(String(seg.date ?? ''));
    if (!segDate) return;
    const from = seg.from_stop_number;
    const to = seg.to_stop_number;
    if (from == null || to == null) return;

    let inSpan = false;
    if (fromDate && toDate && fromDate === toDate) {
      if (segDate !== fromDate) return;
      inSpan = from >= fromStop.stop_number && to <= toStop.stop_number;
    } else if (fromDate && segDate === fromDate) {
      inSpan = from >= fromStop.stop_number;
    } else if (toDate && segDate === toDate) {
      inSpan = to <= toStop.stop_number;
    } else if (fromDate && toDate && segDate > fromDate && segDate < toDate) {
      inSpan = true;
    }
    if (!inSpan) return;
    ids.push(buildRouteSegmentSelectionId(seg.vehicle, seg.date, from, to, idx));
  });
  return ids;
}

/**
 * Green Start on the order map = trunk vehicle telematics start, not the
 * customer collection geocode. Falls back to the first trunk-leg GPS point.
 */
export function resolveTrunkTelematicsStartAnchor(params: {
  pickupVehicleId: string;
  originDate?: string | null;
  destinationDate?: string | null;
  stops: Array<{
    AssetName?: string | null;
    day_date?: string | null;
    stop_type?: string | null;
    stop_number?: number | null;
    latitude?: number | null;
    longitude?: number | null;
  }>;
  trunkSegments: Array<{
    vehicle?: string | null;
    date?: string | null;
    points?: Array<[number, number]>;
    coordinates?: Array<[number, number]>;
  }>;
}): { lat: number; lon: number; vehicleId: string; day_date: string } | null {
  const pickupVid = standardiseVehicleId(params.pickupVehicleId);
  if (!pickupVid) return null;

  const dateSet = new Set(
    [params.originDate, params.destinationDate]
      .map((d) => normalizeTelematicsDate(String(d ?? '')) || getDateOnly(String(d ?? '')))
      .filter(Boolean),
  );

  const validStop = (s: (typeof params.stops)[number]) => {
    const lat = Number(s.latitude);
    const lon = Number(s.longitude);
    return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
  };

  const pickupStops = params.stops
    .filter((s) => standardiseVehicleId(s.AssetName ?? '') === pickupVid && validStop(s))
    .sort((a, b) => (a.stop_number ?? 0) - (b.stop_number ?? 0));

  const dateMatched = dateSet.size > 0
    ? pickupStops.filter((s) => {
      const d = normalizeTelematicsDate(String(s.day_date ?? '')) || getDateOnly(String(s.day_date ?? ''));
      return !d || dateSet.has(d);
    })
    : pickupStops;
  // Prefer date-matched Start; if the day filter excludes everything, still use
  // the pickup vehicle's Start rather than falling back to the collection geocode.
  const candidates = dateMatched.length > 0 ? dateMatched : pickupStops;

  const typedStart = candidates.find(
    (s) => String(s.stop_type ?? '').toLowerCase() === 'start',
  );
  const chosen = typedStart ?? candidates[0];
  if (chosen) {
    return {
      lat: Number(chosen.latitude),
      lon: Number(chosen.longitude),
      vehicleId: pickupVid,
      day_date:
        normalizeTelematicsDate(String(chosen.day_date ?? ''))
        || getDateOnly(String(chosen.day_date ?? ''))
        || String(params.originDate ?? '')
        || '',
    };
  }

  for (const seg of params.trunkSegments) {
    if (standardiseVehicleId(seg.vehicle ?? '') !== pickupVid) continue;
    if (Array.isArray(seg.points) && seg.points.length > 0) {
      const [lat, lon] = seg.points[0]!;
      if (Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0) {
        return {
          lat,
          lon,
          vehicleId: pickupVid,
          day_date: normalizeTelematicsDate(String(seg.date ?? '')) || String(seg.date ?? '') || '',
        };
      }
    }
    if (Array.isArray(seg.coordinates) && seg.coordinates.length > 0) {
      const [lon, lat] = seg.coordinates[0]!;
      if (Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0) {
        return {
          lat,
          lon,
          vehicleId: pickupVid,
          day_date: normalizeTelematicsDate(String(seg.date ?? '')) || String(seg.date ?? '') || '',
        };
      }
    }
  }

  return null;
}

export function buildRouteAnchorText(
  stop: TelematicsDeliveryStop,
  isStart: boolean,
  allAnchors: TelematicsDeliveryStop[],
  routeVehicleId: string | null | undefined,
  trunkVehicleId: string | null | undefined,
  deliveryVehicleId: string | null | undefined,
): string {
  const label = isStart ? 'Start' : 'End';
  const anchorVehicleIds = new Set(
    allAnchors
      .map((anchor) => standardiseVehicleId(anchor.AssetName ?? '') || String(anchor.AssetName ?? '').trim())
      .filter(Boolean),
  );
  if (anchorVehicleIds.size <= 1) return label;

  const stopVehicleId = standardiseVehicleId(stop.AssetName ?? '') || String(stop.AssetName ?? '').trim();
  const routeRole = stopVehicleId && trunkVehicleId && standardiseVehicleId(trunkVehicleId) === stopVehicleId
    ? 'Trunk'
    : stopVehicleId && deliveryVehicleId && standardiseVehicleId(deliveryVehicleId) === stopVehicleId
      ? 'Delivery'
      : routeVehicleId && standardiseVehicleId(routeVehicleId) === stopVehicleId
        ? 'Delivery'
        : 'Vehicle';

  return `${label} (${routeRole} vehicle${stopVehicleId ? ` - ${stopVehicleId}` : ''})`;
}

export function calculatePolylineDistanceKm(points: Array<[number, number]>): number {
  if (!Array.isArray(points) || points.length < 2) return 0;

  let totalKm = 0;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    if (!prev || !curr) continue;
    const prevLat = Number(prev[0]);
    const prevLon = Number(prev[1]);
    const currLat = Number(curr[0]);
    const currLon = Number(curr[1]);
    if (!Number.isFinite(prevLat) || !Number.isFinite(prevLon) || !Number.isFinite(currLat) || !Number.isFinite(currLon)) continue;
    totalKm += haversineMetres(prevLat, prevLon, currLat, currLon) / 1000;
  }

  return Number.isFinite(totalKm) ? totalKm : 0;
}

/** UK fleet UI uses miles. Polyline haversine is computed in km — convert for display / £/mile. */
export const KM_TO_MILES = 0.621371;

export function calculatePolylineDistanceMiles(points: Array<[number, number]>): number {
  return calculatePolylineDistanceKm(points) * KM_TO_MILES;
}

export function buildOrderRouteLegLabel(kind: 'start' | 'trunk' | 'delivery' | 'end'): string {
  switch (kind) {
    case 'start':
      return 'Start (Trunk starting point)';
    case 'trunk':
      return 'Trunk leg';
    case 'delivery':
      return 'Delivery leg';
    case 'end':
      return 'End (Delivery ending point)';
    default:
      return 'Leg';
  }
}

export function buildOrderRouteSegments(
  orderPickupPoint: { lat: number; lon: number } | null,
  orderHandoffPoint: OrderHandoffMapPoint | null,
  orderDeliveryPoint: { lat: number; lon: number } | null,
): Array<{ id: string; label: string; coordinates: [number, number][] }> {
  const segments: Array<{ id: string; label: string; coordinates: [number, number][] }> = [];

  if (orderPickupPoint && orderHandoffPoint) {
    segments.push({
      id: 'order-trunk-leg',
      label: 'Trunk leg',
      coordinates: [
        [orderPickupPoint.lon, orderPickupPoint.lat],
        [orderHandoffPoint.lon, orderHandoffPoint.lat],
      ],
    });
  }

  if (orderHandoffPoint && orderDeliveryPoint) {
    segments.push({
      id: 'order-delivery-leg',
      label: 'Delivery leg',
      coordinates: [
        [orderHandoffPoint.lon, orderHandoffPoint.lat],
        [orderDeliveryPoint.lon, orderDeliveryPoint.lat],
      ],
    });
  }

  if (!orderHandoffPoint && orderPickupPoint && orderDeliveryPoint) {
    segments.push({
      id: 'order-direct-leg',
      label: 'Order leg',
      coordinates: [
        [orderPickupPoint.lon, orderPickupPoint.lat],
        [orderDeliveryPoint.lon, orderDeliveryPoint.lat],
      ],
    });
  }

  return segments;
}

export function resolveOrderRouteSegmentsForMap(
  order: Partial<ProfitMapOrder> | null,
  routeSegments: RouteSegment[],
  preferredDate?: string,
): RouteSegment[] {
  if (!order) return [];

  const vehicleCandidates = Array.from(new Set(
    [
      order.vehicle_observed,
      order.vehicle_assigned,
      order.vehicle_pickup,
      order.vehicleId,
    ]
      .map((value) => standardiseVehicleId(value ?? ''))
      .filter(Boolean) as string[],
  ));

  const dateCandidates = Array.from(new Set(
    [
      preferredDate,
      order.destination_end_timestamp_local,
      order.destination_timestamp,
      order.origin_end_timestamp_local,
      order.origin_end_timestamp,
      order.date,
    ]
      .map((value) => normalizeTelematicsDate(value ?? '') || getDateOnly(value ?? ''))
      .filter(Boolean) as string[],
  ));

  if (vehicleCandidates.length === 0 || dateCandidates.length === 0) {
    return [];
  }

  const exactMatches = routeSegments.filter((segment) => {
    const segmentVehicle = standardiseVehicleId(segment.vehicle);
    const segmentDate = normalizeTelematicsDate(segment.date) || getDateOnly(segment.date);
    if (!segmentVehicle || !segmentDate) return false;
    return vehicleCandidates.includes(segmentVehicle) && dateCandidates.includes(segmentDate);
  });

  if (exactMatches.length > 0) {
    return exactMatches;
  }

  return routeSegments.filter((segment) => {
    const segmentVehicle = standardiseVehicleId(segment.vehicle);
    if (!segmentVehicle) return false;
    return vehicleCandidates.includes(segmentVehicle);
  });
}

function pointWithinOrderRouteWindow(
  point: { lat: number; lon: number },
  pickupPoint: { lat: number; lon: number },
  deliveryPoint: { lat: number; lon: number },
  corridorThresholdMetres: number,
): boolean {
  const startToPoint = haversineMetres(pickupPoint.lat, pickupPoint.lon, point.lat, point.lon);
  const endToPoint = haversineMetres(deliveryPoint.lat, deliveryPoint.lon, point.lat, point.lon);
  const routeDistance = haversineMetres(pickupPoint.lat, pickupPoint.lon, deliveryPoint.lat, deliveryPoint.lon);

  return startToPoint <= corridorThresholdMetres || endToPoint <= corridorThresholdMetres || (startToPoint + endToPoint <= routeDistance + corridorThresholdMetres);
}

export function clipOrderRouteSegmentsToOrderWindow(
  segments: Array<{ points?: Array<[number, number]>; coordinates?: Array<[number, number]> }>,
  pickupPoint: { lat: number; lon: number } | null,
  deliveryPoint: { lat: number; lon: number } | null,
): Array<{ points?: Array<[number, number]>; coordinates?: Array<[number, number]> }> {
  if (!pickupPoint || !deliveryPoint) return segments;

  const routeDistance = haversineMetres(pickupPoint.lat, pickupPoint.lon, deliveryPoint.lat, deliveryPoint.lon);
  const corridorThresholdMetres = Math.max(2500, routeDistance * 0.15 + 1000);

  return segments.filter((segment) => {
    const rawPoints = Array.isArray(segment.points)
      ? segment.points
      : Array.isArray(segment.coordinates)
        ? segment.coordinates.map(([lon, lat]) => [lat, lon] as [number, number])
        : [];

    if (rawPoints.length === 0) return false;

    return rawPoints.some(([lat, lon]) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
      return pointWithinOrderRouteWindow({ lat, lon }, pickupPoint, deliveryPoint, corridorThresholdMetres);
    });
  });
}

function segmentPointsLatLon(
  segment: { points?: Array<[number, number]>; coordinates?: Array<[number, number]> },
): Array<[number, number]> {
  if (Array.isArray(segment.points) && segment.points.length > 0) {
    return segment.points.filter(
      (point): point is [number, number] =>
        Array.isArray(point)
        && point.length >= 2
        && Number.isFinite(point[0])
        && Number.isFinite(point[1])
        && point[0] !== 0
        && point[1] !== 0,
    );
  }
  if (Array.isArray(segment.coordinates) && segment.coordinates.length > 0) {
    return segment.coordinates
      .filter(
        (point): point is [number, number] =>
          Array.isArray(point)
          && point.length >= 2
          && Number.isFinite(point[0])
          && Number.isFinite(point[1])
          && point[0] !== 0
          && point[1] !== 0,
      )
      .map(([lon, lat]) => [lat, lon] as [number, number]);
  }
  return [];
}

function parseSegmentTimeMs(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const t = Date.parse(normalizeUtcTimestamp(String(raw)));
  return Number.isFinite(t) ? t : null;
}

/**
 * Collapse a vehicle's telematics segments into the single order-relevant path
 * between two anchors (e.g. Start→Handoff or Handoff→End).
 *
 * End = first GPS sample that is nearly as close as the best approach after
 * leaving the origin (so a later return-to-hub does not win). Then extend the
 * polyline to the anchor pins so the map does not leave gaps at hub/delivery.
 *
 * Preserves the sliced window's real GPS timestamps for replay.
 */
export function extractOrderLegRouteSegment<T extends {
  points?: Array<[number, number]>;
  coordinates?: Array<[number, number]>;
  vehicle?: string | null;
  date?: string | null;
  from_stop_number?: number | null;
  to_stop_number?: number | null;
  label?: string | null;
  id?: string | null;
  segment_start?: string | null;
  segment_end?: string | null;
}>(
  segments: T[],
  fromPt: { lat: number; lon: number } | null,
  toPt: { lat: number; lon: number } | null,
  options: { label: string; vehicle?: string | null },
): (T & {
  label: string;
  points: Array<[number, number]>;
  id: string;
  segment_start?: string;
  segment_end?: string;
}) | null {
  if (!fromPt || !toPt || segments.length === 0) return null;

  const sorted = [...segments].sort((a, b) => {
    const dateA = String(a.date ?? '');
    const dateB = String(b.date ?? '');
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return (a.from_stop_number ?? 0) - (b.from_stop_number ?? 0);
  });

  type TimedFlat = { lat: number; lon: number; t: number | null };
  const flat: TimedFlat[] = [];
  for (const seg of sorted) {
    const pts = segmentPointsLatLon(seg);
    if (pts.length === 0) continue;
    const startMs = parseSegmentTimeMs(seg.segment_start ?? null);
    const endMs = parseSegmentTimeMs(seg.segment_end ?? null);
    for (let i = 0; i < pts.length; i += 1) {
      const [lat, lon] = pts[i]!;
      let t: number | null = null;
      if (startMs != null && endMs != null) {
        const span = Math.max(1, endMs - startMs);
        t = startMs + (span * i) / Math.max(1, pts.length - 1);
      }
      flat.push({ lat, lon, t });
    }
  }
  if (flat.length < 2) return null;

  const routeDistance = haversineMetres(fromPt.lat, fromPt.lon, toPt.lat, toPt.lon);
  const startSnapThreshold = Math.max(40_000, routeDistance * 0.5 + 10_000);
  // Origin / destination "at the pin" — keep tight so we do not cut miles early.
  const pinRadiusM = Math.max(400, Math.min(2_000, routeDistance * 0.05 + 400));
  const leaveRadiusM = Math.max(800, pinRadiusM);

  const distTo = (i: number, target: { lat: number; lon: number }) =>
    haversineMetres(flat[i]!.lat, flat[i]!.lon, target.lat, target.lon);

  const closestIndex = (
    target: { lat: number; lon: number },
    fromInclusive: number,
    toExclusive: number,
  ): { idx: number; dist: number } => {
    let idx = fromInclusive;
    let dist = Infinity;
    const lo = Math.max(0, fromInclusive);
    const hi = Math.min(flat.length, toExclusive);
    for (let i = lo; i < hi; i += 1) {
      const d = distTo(i, target);
      if (d < dist) {
        dist = d;
        idx = i;
      }
    }
    return { idx, dist };
  };

  /** First index within pinRadius of target; else closest in range. */
  const firstNearPinIndex = (
    target: { lat: number; lon: number },
    fromInclusive: number,
  ): { idx: number; dist: number } => {
    const lo = Math.max(0, fromInclusive);
    for (let i = lo; i < flat.length; i += 1) {
      const d = distTo(i, target);
      if (d <= pinRadiusM) return { idx: i, dist: d };
    }
    return closestIndex(target, lo, flat.length);
  };

  /**
   * After leaving origin: find the best (min) distance to destination, then take
   * the first sample that is nearly that close. Morning hub arrival wins over a
   * later closer revisit.
   */
  const firstNearBestApproachIndex = (
    target: { lat: number; lon: number },
    fromInclusive: number,
  ): { idx: number; dist: number } => {
    const lo = Math.max(0, fromInclusive);
    if (lo >= flat.length) return { idx: lo, dist: Infinity };
    const best = closestIndex(target, lo, flat.length);
    if (!Number.isFinite(best.dist)) return best;
    // Allow a little slack so the first inbound approach counts even if a
    // later dwell sits slightly closer to the geocoded pin.
    const threshold = Math.max(best.dist + 750, best.dist * 1.35, pinRadiusM);
    for (let i = lo; i < flat.length; i += 1) {
      const d = distTo(i, target);
      if (d <= threshold) return { idx: i, dist: d };
    }
    return best;
  };

  // First visit to the origin (not a later return-to-hub that is slightly closer).
  const start = firstNearPinIndex(fromPt, 0);
  if (start.dist > startSnapThreshold) return null;

  // Skip residual dwell on the origin before the vehicle actually moves away.
  let departIdx = start.idx;
  for (let i = start.idx; i < flat.length; i += 1) {
    if (distTo(i, fromPt) > leaveRadiusM) {
      departIdx = i;
      break;
    }
  }

  const end = firstNearBestApproachIndex(toPt, Math.max(departIdx, start.idx + 1));
  if (end.idx <= start.idx) return null;
  // Allow a far geocoded destination (soft snap) as long as we left the origin
  // and found a progressive best approach on the GPS track.
  if (end.dist > Math.max(startSnapThreshold, routeDistance + 15_000)) return null;

  let slicedTimed = flat.slice(start.idx, end.idx + 1);
  if (slicedTimed.length < 2) return null;

  // Timestamps from GPS slice before pin snaps (arrival = last real telematics sample).
  const firstT = slicedTimed.find((p) => p.t != null)?.t ?? null;
  const lastT = [...slicedTimed].reverse().find((p) => p.t != null)?.t ?? null;
  let segmentStart: string | undefined;
  let segmentEnd: string | undefined;
  if (firstT != null && lastT != null) {
    const endMs = lastT > firstT ? lastT : firstT + 60_000;
    segmentStart = new Date(firstT).toISOString();
    segmentEnd = new Date(endMs).toISOString();
  }

  // Snap polyline ends to order anchors so the map does not leave gaps between
  // telematics and handoff/delivery pins (GPS path often stops short of geocode).
  const SNAP_GAP_M = 80;
  const first = slicedTimed[0]!;
  const last = slicedTimed[slicedTimed.length - 1]!;
  if (haversineMetres(first.lat, first.lon, fromPt.lat, fromPt.lon) > SNAP_GAP_M) {
    slicedTimed = [{ lat: fromPt.lat, lon: fromPt.lon, t: first.t }, ...slicedTimed];
  }
  if (haversineMetres(last.lat, last.lon, toPt.lat, toPt.lon) > SNAP_GAP_M) {
    slicedTimed = [...slicedTimed, { lat: toPt.lat, lon: toPt.lon, t: last.t }];
  }

  const template = sorted[0]!;
  const vehicle = options.vehicle || template.vehicle || '';
  const idSlug = options.label.trim().toLowerCase().replace(/\s+/g, '-') || 'leg';
  return {
    ...template,
    id: `order-${idSlug}`,
    label: options.label,
    vehicle,
    from_stop_number: 1,
    to_stop_number: 2,
    points: slicedTimed.map((p) => [p.lat, p.lon] as [number, number]),
    coordinates: undefined,
    segment_start: segmentStart ?? template.segment_start ?? undefined,
    segment_end: segmentEnd ?? template.segment_end ?? undefined,
  };
}

/**
 * Last-resort telematics merge: keep GPS points near either anchor or the
 * corridor, then slice start→end. Used when strict extract fails but the
 * vehicle still has points in the order window (avoids straight-line fallback).
 */
export function mergeOrderLegTelematicsNearAnchors<T extends {
  points?: Array<[number, number]>;
  coordinates?: Array<[number, number]>;
  vehicle?: string | null;
  date?: string | null;
  from_stop_number?: number | null;
  label?: string | null;
}>(
  segments: T[],
  fromPt: { lat: number; lon: number } | null,
  toPt: { lat: number; lon: number } | null,
  options: { label: string; vehicle?: string | null },
): (T & { label: string; points: Array<[number, number]>; id: string }) | null {
  if (!fromPt || !toPt || segments.length === 0) return null;

  const sorted = [...segments].sort((a, b) => {
    const dateA = String(a.date ?? '');
    const dateB = String(b.date ?? '');
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return (a.from_stop_number ?? 0) - (b.from_stop_number ?? 0);
  });

  const flat: Array<[number, number]> = [];
  for (const seg of sorted) {
    for (const point of segmentPointsLatLon(seg)) flat.push(point);
  }
  if (flat.length < 2) return null;

  const routeDistance = haversineMetres(fromPt.lat, fromPt.lon, toPt.lat, toPt.lon);
  const keepRadius = Math.max(15_000, routeDistance * 0.5 + 8_000);

  const kept = flat.filter(([lat, lon]) => {
    const dFrom = haversineMetres(lat, lon, fromPt.lat, fromPt.lon);
    const dTo = haversineMetres(lat, lon, toPt.lat, toPt.lon);
    return (
      dFrom <= keepRadius
      || dTo <= keepRadius
      || pointWithinOrderRouteWindow({ lat, lon }, fromPt, toPt, keepRadius)
    );
  });
  if (kept.length < 2) return null;

  // Re-run extract on the kept point cloud wrapped as one segment.
  return extractOrderLegRouteSegment(
    [{ ...sorted[0]!, points: kept, coordinates: undefined }] as T[],
    fromPt,
    toPt,
    options,
  );
}

/** Build a straight-line order leg when telematics cannot be clipped to anchors. */
export function buildSyntheticOrderLegSegment(
  fromPt: { lat: number; lon: number },
  toPt: { lat: number; lon: number },
  options: { label: string; vehicle?: string | null; id?: string },
): {
  id: string;
  label: string;
  vehicle: string;
  date: string;
  from_stop_number: number;
  to_stop_number: number;
  coordinates: Array<[number, number]>;
  points?: undefined;
} {
  const idSlug = (options.id ?? options.label).trim().toLowerCase().replace(/\s+/g, '-') || 'leg';
  return {
    id: idSlug.startsWith('order-') ? idSlug : `order-${idSlug}`,
    label: options.label,
    vehicle: options.vehicle ?? '',
    date: '',
    from_stop_number: 1,
    to_stop_number: 2,
    coordinates: [
      [fromPt.lon, fromPt.lat],
      [toPt.lon, toPt.lat],
    ],
  };
}

/**
 * MapLibre wants [lon, lat]. Telematics segs store `points` as [lat, lon];
 * synthetic order legs store `coordinates` as [lon, lat].
 */
export function segmentCoordinatesForMapRoute(
  seg: { points?: Array<[number, number]>; coordinates?: Array<[number, number]> },
): [number, number][] {
  if (Array.isArray(seg.coordinates) && seg.coordinates.length >= 2) {
    return seg.coordinates.filter(
      (point): point is [number, number] =>
        Array.isArray(point)
        && point.length >= 2
        && Number.isFinite(point[0])
        && Number.isFinite(point[1])
        && point[0] !== 0
        && point[1] !== 0,
    );
  }
  if (Array.isArray(seg.points) && seg.points.length >= 2) {
    return seg.points
      .filter(
        ([lat, lon]) =>
          lat != null
          && lon != null
          && Number.isFinite(lat)
          && Number.isFinite(lon)
          && lat !== 0
          && lon !== 0,
      )
      .map(([lat, lon]) => [lon, lat] as [number, number]);
  }
  return [];
}

/**
 * Fallback (straight-line) legs for a job another depot delivers: keep only our
 * leg to the handoff depot, as a Collection leg. The hub → customer leg is the
 * other depot's, and with no handoff there is no leg of ours we can place.
 */
export function collectionOnlyRouteSegments<T extends { id: string; label: string }>(segments: T[]): T[] {
  return segments
    .filter((seg) => seg.id === 'order-trunk-leg')
    .map((seg) => ({ ...seg, label: 'Collection leg' }));
}

/**
 * Where to clip our single leg of an order. Delivered by another depot: at the
 * handoff depot; if that can't be placed, at the delivery address as before
 * (never unclipped — that would draw the vehicle's whole day).
 */
export function orderLegClipEnd(
  deliveredByOtherDepot: boolean,
  handoffPoint: { lat: number; lon: number } | null,
  deliveryPoint: { lat: number; lon: number } | null,
): { lat: number; lon: number } | null {
  const handoff = handoffPoint ? { lat: handoffPoint.lat, lon: handoffPoint.lon } : null;
  return deliveredByOtherDepot ? (handoff ?? deliveryPoint) : (deliveryPoint ?? handoff);
}
